/**
 * Lives (spec 02 §3 Death, Respawn, Join, Continues), ported from rebb64: the death animation
 * (spawn_animation $28A5, state $0E), the respawn invulnerability (spawn_timer $290D, state $10), and
 * the main loop's check_player_state ($045C, state $0F) and check_join_game ($052A).
 */

import { resetHurry } from '../flow';
import { calmAll } from '../monsters';
import { passesDone } from './blow';
import { JOY_FIRE, losePowerups } from './state';
import {
  CALM_MIN_MONSTERS, DEATH_FALL, DEATH_TIMER, DEATH_TIMER_END, INVULNERABLE_TICKS, JOIN_LIVES, START_FRAME,
  START_X, START_Y, WRAP_BOTTOM, WRAP_TOP,
} from './tuning';
import type { Player, PlayerId, World } from '../types';

/** Put a player at the level start, standing, facing in; a new level also ends any invulnerability ($15FB). */
export function placeAtStart(world: World, id: PlayerId): void {
  const p = world.players[id];
  p.x = START_X[id];
  p.y = START_Y;
  p.facing = id === 0 ? 1 : -1;
  p.motion.frame = START_FRAME[id];
  p.invulnerable = 0;
  p.motion.flash = false;
}

/** Touched by something deadly: state $0E, from the next tick the death animation. Not while invulnerable. */
export function killPlayer(world: World, id: PlayerId): void {
  const p = world.players[id];
  if (p.state !== 'play' || p.invulnerable > 0) return;
  p.state = 'dying';
  p.motion.dead = false;
  world.events.push({ type: 'playerDied', player: id });
}

/**
 * spawn_animation: the timer runs from $20 down through 0 and on to $C8, 88 ticks. For its first 32 the
 * body spins (frames $E, $D, $C, 0) and, if it was airborne, falls 3 px per tick through everything,
 * wrapping at $F5; then it lies dead (frames $12..$F) until the main loop takes it (state $0F).
 */
export function deathTick(world: World, p: Player): void {
  const m = p.motion;
  m.shoe = false;
  if (!m.deathStarted) {
    m.deathStarted = true;
    m.anim = DEATH_TIMER;
  }
  m.anim = (m.anim - 1) & 0xff;
  const phase = (m.anim >> 1) & 3;
  if (m.anim & 0x80) {
    m.frame = phase + 0x0f;
    if (m.anim === DEATH_TIMER_END) {
      m.dead = true;
      m.deadAt = passesDone(world);
    }
    return;
  }
  m.frame = phase === 0 ? 0 : phase + 0x0b;
  if (m.jump >= 0 || m.falling) {
    const y = p.y + DEATH_FALL;
    p.y = y >= WRAP_BOTTOM ? WRAP_TOP : y;
  }
}

/** spawn_timer (state $10): a tick of invulnerability, the sprite colour flashing; the player acts as usual. */
export function spawnTimer(p: Player): void {
  p.invulnerable--;
  p.motion.flash = !p.motion.flash;
}

/**
 * The main loop's part, run after the players' tick: a player whose death animation ended before the
 * last pass (state $0F) respawns or is out (check_player_state); then an idle player holding fire joins
 * (check_join_game). With the C64 continue bug, joining is possible only while someone is still in the
 * game or on the pass the last life went - otherwise that pass ended the game (`$0A64`).
 */
export function mainLoop(world: World): void {
  let lastLifeGone = false;
  for (const p of [world.players[1], world.players[0]]) {
    if (p.state === 'dying' && p.motion.dead && passesDone(world) > p.motion.deadAt) {
      if (!lifeEnds(world, p)) lastLifeGone = true;
    }
  }
  const inGame = world.players.some((p) => p.state === 'play' || p.state === 'dying');
  if (world.options.c64Continue && !inGame && !lastLifeGone) return;
  for (const p of [world.players[1], world.players[0]]) {
    if ((p.state === 'out' || p.state === 'gameOver') && (p.motion.joy & JOY_FIRE) !== 0) join(world, p);
  }
}

/**
 * check_player_state for a player in state $0F (a death, or a join): power-ups lost (D_7F53), the
 * jump and fall cleared, a life taken. With one left: back at the start, invulnerable for $4A ticks.
 * Without: out (GAME OVER). Either way the HURRY UP countdown restarts and, with 2 or more monsters
 * left, all calm down - at this point, the end of the death animation, not at the moment of death.
 * Returns whether the player is back in play.
 */
function lifeEnds(world: World, p: Player): boolean {
  const m = p.motion;
  losePowerups(m);
  m.deathStarted = false;
  m.dead = false;
  m.jump = -1;
  m.descent = -1;
  m.falling = false;
  p.lives--;
  const alive = p.lives >= 0;
  if (alive) {
    placeAtStart(world, p.id);
    p.invulnerable = INVULNERABLE_TICKS;
    p.state = 'play';
    world.events.push({ type: 'respawn', player: p.id });
  } else {
    p.lives = 0;
    p.x = 0;
    p.y = 0;
    m.frame = START_FRAME[p.id];
    p.state = 'gameOver';
    world.events.push({ type: 'gameOver', player: p.id });
  }
  resetHurry(world);
  if (world.monsters.filter((mo) => mo.state !== 'dead').length >= CALM_MIN_MONSTERS) calmAll(world);
  return alive;
}

/**
 * check_join_game: costs a continue (none left: no join), score and extra-life count reset, the lives
 * counter set to 4 and state $0F - which check_player_state turns into a respawn with 1 + 3 lives. The
 * C64 does that on its next pass; here at once (the sprite is off screen meanwhile).
 */
function join(world: World, p: Player): void {
  if (world.continues <= 0) return;
  world.continues--;
  p.score = 0;
  p.nextExtraLife = 0;
  p.lives = JOIN_LIVES;
  lifeEnds(world, p);
}
